<template>
    <standard-widget
        widgetTitle="Recent Students"
        :cardHeight="launchpadWidgetHeights.secondRow"
        :thumbnail="null"
        cardStyle="margin-top: 0 !important; overflow: hidden;"
    >
        <template v-slot:widget-body>
            <div v-if="!photoURL && reloading">
                <fe-spinner class="spinner"/>
            </div>
            <div v-else class="student-image-container">
                <fe-tooltip
                    v-for="item in studentImages"
                    :key="item.id"
                    :tooltip="item.student_full_name"
                >
                    <div>
                        <div v-if="item.isImage" class="outer-student-circle"
                            tabindex="0"
                            role="button"
                            :aria-label="`View ${item.student_full_name}'s student profile`"
                            @click="goToStudentProfile(item.id)"
                            @keydown.enter="goToStudentProfile(item.id)"
                            @keydown.space.prevent="goToStudentProfile(item.id)"
                            data-test="recent-student-icon"
                        >
                            <div class="student-img">
                                <div :style="item.styles"></div>
                            </div>
                        </div>
                        <div v-else class="outer-student-circle"
                            tabindex="0"
                            role="button"
                            :aria-label="`View ${item.student_full_name}'s student profile`"
                            @click="goToStudentProfile(item.id)"
                            @keydown.enter="goToStudentProfile(item.id)"
                            @keydown.space.prevent="goToStudentProfile(item.id)"
                            data-test="recent-student-icon"
                        >
                            <div class="inner-student-circle">
                                {{ item.initials }}
                            </div>
                        </div>
                    </div>
                </fe-tooltip>
            </div>
        </template>
    </standard-widget>
</template>

<script>
import { mapState } from 'vuex'
import StandardWidget from './StandardWidget.vue';

export default {
    name: 'StudentCard',

    components: {
        StandardWidget
    },

    data() {
        return {
            reloading: true,
            studentImages: [],
            photoURL: false,
            tools: [],
            thumbnail: {
                icon: 'far fa-address-card'
            },
        }
    },

    computed: {
        ...mapState('global', ['uiSavedSearch', 'lastLaunchpadVisit', 'launchpadWidgetHeights']),
    },

    mounted() {
        this.loadData()
    },

    methods: {
        loadData() {
            this.reloading = true
            this.$getUiSavedSearch('RECENT_STUDENTS')
            if(this.uiSavedSearch) {
                this.generateSpriteSheet()
            }
        },
        generateSpriteSheet() {
            let ss = this.uiSavedSearch.RECENT_STUDENTS || []
            let ids = ss?.length && ss[0].arguments.map(x => x.argument_value)

            if(ids) {
                ids = this.$_.uniq(ids).slice(0,20)

                // Students for whom an image cannot be generated for any reason can
                // fall back to the generic silhouette
                this.$fetchStudentImages(ids, 64, null, true, true).then((obj) => {
                    this.photoURL = obj.img

                    // Preserve the order of the saved search / recent students
                    this.studentImages = ids.map(studentId => obj.images.find(img => img.id == studentId)).filter(itm => !!itm).reverse().slice(0, 16)

                    this.reloading = false
                })
            } else {
                this.reloading = false
            }
        },
        goToStudentProfile(id) {
            if(id) {
                this.$router.push({path: '/StudentProfile/' + id})
            }
        }
    },

    watch: {
        uiSavedSearch(v) {
            if(v) {
                this.generateSpriteSheet()
            } else {
                this.reloading = false
            }
        },
        lastLaunchpadVisit(v) {
            if (!this.reloading) {
                this.reloading = true
                this.loadData()
            }
        },
    }
}
</script>

<style lang="scss" scoped>

.spinner {
    position: absolute;
    left: 0;
    right: 0;
    margin: auto;
    line-height: 250px !important;
}

.student-image-container {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    padding: 8px 16px;
    place-items: center;
    row-gap: 8px;
    gap: 0;
}

.outer-student-circle {
    display: flex;
    justify-content: center;
    align-items: center;
    width: 72px;
    height: 72px;
    border-radius: 50%;
    border: 2px solid #e0e1e6;
    background-color: transparent;
    cursor: pointer;

    &:focus {
        outline: 2px solid #2B87FF;
        outline-offset: 2px;
    }
}

.student-img {
    width: 64px;
    height: 64px;
    border-radius: 50%;
    cursor: pointer;
    overflow: hidden;
}

.inner-student-circle {
    display: flex;
    justify-content: center;
    align-items: center;
    width: 64px;
    height: 64px;
    border-radius: 50%;
    background-color: #495b8f;
    color: #fff;
    font-size: 16px;
    font-weight: bold;
}

// XL: > 1520px
@media (min-width: 1521px) {
    .student-image-container {
        row-gap: 16px;
    }
}

// L: 1401px - 1520px
@media (min-width: 1401px) and (max-width: 1520px) {
    .outer-student-circle {
        width: 63px;
        height: 63px;
    }
    .student-img {
        width: 56px;
        height: 56px;
    }
    .inner-student-circle {
        width: 56px;
        height: 56px;
        font-size: 14px;
    }
    .student-image-container {
        row-gap: 34px;
        padding: 2px 14px;
    }
}

// M: 1301px - 1400px
@media (min-width: 1301px) and (max-width: 1400px) {
    .outer-student-circle {
        width: 54px;
        height: 54px;
    }
    .student-img {
        width: 48px;
        height: 48px;
    }
    .inner-student-circle {
        width: 48px;
        height: 48px;
        font-size: 12px;
    }
    .student-image-container {
        row-gap: 40px;
        padding: 8px 14px;
    }
}

// S: 1051px - 1300px
@media (min-width: 1051px) and (max-width: 1300px) {
    .outer-student-circle {
        width: 48px;
        height: 48px;
    }
    .student-img {
        width: 42px;
        height: 42px;
    }
    .inner-student-circle {
        width: 42px;
        height: 42px;
        font-size: 11px;
    }
    .student-image-container {
        row-gap: 40px;
        padding: 20px 8px;
    }
}

// XS: <= 1050px
@media (max-width: 1050px) {
    .outer-student-circle {
        width: 42px;
        height: 42px;
    }
    .student-img {
        width: 36px;
        height: 36px;
    }
    .inner-student-circle {
        width: 36px;
        height: 36px;
        font-size: 11px;
    }
    .student-image-container {
        row-gap: 44px;
        padding: 20px 4px;
    }
}

</style>
